// API-Client für alle drei Plattformen (Besitzer: api-kern): JSON, Fehlerformat aus SPEC §7,
// Cookie-Sitzung (same-origin), im Modus supabase Bearer-Token, im Modus demo Persona über ?as=.
import { apiErrorSchema, type ErrorCode } from '@bar/contracts';

export type ClientErrorCode = ErrorCode | 'NETWORK_ERROR' | 'BAD_RESPONSE';

export interface ErrorDetail {
  path: string;
  message: string;
}

/** Fehler einer API-Antwort (oder des Netzes, dann status 0). */
export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: ClientErrorCode,
    message: string,
    readonly details: ErrorDetail[] = [],
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }

  get isUnauthenticated() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isNotFound() {
    return this.status === 404;
  }
  /** Feldfehler als Map für react-hook-form (`setError(path, …)`). */
  fieldErrors(): Record<string, string> {
    return Object.fromEntries(this.details.map((d) => [d.path, d.message]));
  }
}

export const isApiError = (error: unknown): error is ApiClientError =>
  error instanceof ApiClientError;

// ── Bearer (Modus supabase) ─────────────────────────────────────────────────
type TokenProvider = () => Promise<string | undefined>;
let accessTokenProvider: TokenProvider | undefined;

/** Liefert bei jedem Request das aktuelle Access-Token (Supabase); undefined = Cookie-Sitzung. */
export function setAccessTokenProvider(provider: TokenProvider | undefined) {
  accessTokenProvider = provider;
}

export async function currentAccessToken(): Promise<string | undefined> {
  return accessTokenProvider?.();
}

// ── Persona (Modus demo, `?as=<persona>`) ───────────────────────────────────
const PERSONA_KEY = 'bar.as';
const PERSONA_RE = /^[a-z0-9._-]{1,64}$/i;

function storage(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

/** Liest `?as=` aus der URL und merkt es für diesen Tab; liefert die aktive Persona. */
export function initPersonaFromUrl(search = window.location.search): string | undefined {
  const fromUrl = new URLSearchParams(search).get('as');
  if (fromUrl && PERSONA_RE.test(fromUrl)) storage()?.setItem(PERSONA_KEY, fromUrl);
  return personaOverride();
}

export function personaOverride(): string | undefined {
  const value = storage()?.getItem(PERSONA_KEY) ?? undefined;
  return value && PERSONA_RE.test(value) ? value : undefined;
}

export function clearPersonaOverride() {
  storage()?.removeItem(PERSONA_KEY);
}

// ── Requests ────────────────────────────────────────────────────────────────
export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export function buildUrl(path: string, query?: RequestOptions['query']): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}${path.includes('?') ? '&' : '?'}${qs}` : path;
}

async function toError(res: Response): Promise<ApiClientError> {
  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    payload = undefined;
  }
  const parsed = apiErrorSchema.safeParse(payload);
  if (parsed.success) {
    const { code, message, details, requestId } = parsed.data.error;
    return new ApiClientError(res.status, code, message, details ?? [], requestId);
  }
  return new ApiClientError(res.status, 'BAD_RESPONSE', `Request failed (${res.status})`);
}

/** fetch mit Fehlerformat; liefert das JSON (bzw. undefined bei 204). */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers({ accept: 'application/json', ...options.headers });
  if (options.body !== undefined) headers.set('content-type', 'application/json');
  const persona = personaOverride();
  if (persona) headers.set('x-bar-as', persona);
  const token = await currentAccessToken();
  if (token) headers.set('authorization', `Bearer ${token}`);

  let res: Response;
  try {
    res = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      credentials: 'same-origin',
      signal: options.signal,
    });
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') throw error;
    throw new ApiClientError(0, 'NETWORK_ERROR', 'Network error. Check your connection and retry.');
  }
  if (!res.ok) throw await toError(res);
  if (res.status === 204) return undefined as T;
  const type = res.headers.get('content-type') ?? '';
  if (!type.includes('application/json')) return (await res.text()) as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, options),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T = void>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
};

/** Retry-Regel für TanStack Query: Netz- und 5xx-Fehler höchstens einmal, 4xx nie. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (isApiError(error) && error.status >= 400 && error.status < 500) return false;
  return failureCount < 1;
}
